import { io, Socket } from 'socket.io-client';
import * as Sentry from '@sentry/react-native';
import { getApiBaseUrl } from '@/lib/api-url';
import { getActingProviderId } from '@/store/actingProviderStore';

let socket: Socket | null = null;
let currentToken: string | null = null;
let currentActingProviderId: string | null = null;

/**
 * Socket singleton para a camada realtime genérica do backend (namespace
 * `/realtime`). Reconecta quando o token muda ou quando o dono troca o
 * profissional em foco — a sala do socket é a do provider em foco; qualquer
 * feature pode ouvir eventos via `useRealtimeEvent`.
 */
export function connectRealtime(token: string | null): Socket | null {
  const base = getApiBaseUrl();
  if (!base || !token) {
    disconnectRealtime();
    return null;
  }
  const actingProviderId = getActingProviderId();
  if (socket && currentToken === token && currentActingProviderId === actingProviderId) {
    return socket;
  }

  disconnectRealtime();
  currentToken = token;
  currentActingProviderId = actingProviderId;
  socket = io(`${base}/realtime`, {
    transports: ['websocket'],
    auth: actingProviderId ? { token, actingProviderId } : { token },
    autoConnect: true,
  });
  socket.on('connect', () => {
    Sentry.addBreadcrumb({ category: 'realtime', message: 'connect', level: 'info' });
  });
  socket.on('disconnect', (reason) => {
    Sentry.addBreadcrumb({
      category: 'realtime',
      message: 'disconnect',
      level: 'info',
      data: { reason },
    });
  });
  socket.on('connect_error', (error) => {
    Sentry.addBreadcrumb({
      category: 'realtime',
      message: 'connect_error',
      level: 'error',
      data: { message: error?.message },
    });
  });
  return socket;
}

export function disconnectRealtime(): void {
  if (socket) {
    // Removidos os listeners de 'connect'/'disconnect'/'connect_error' antes do
    // disconnect() de baixo — por isso a desconexão voluntária vira breadcrumb aqui.
    Sentry.addBreadcrumb({
      category: 'realtime',
      message: 'disconnect',
      level: 'info',
      data: { reason: 'client disconnect' },
    });
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  currentToken = null;
  currentActingProviderId = null;
}

export function getRealtimeSocket(): Socket | null {
  return socket;
}
